import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, Divider, Collapse, Tag, Select, Icon, message, Tooltip, Spin, Checkbox } from 'antd';
import lodash from 'lodash';
import InlineGuidingTextEditor from './InlineGuidingTextEditor';
import GroupCreationPanel from './GroupCreationPanel';

const { Panel } = Collapse;

// Constants
const MAX_ATTACHMENTS = 5;
const MAX_PACKAGE_CODE_LENGTH = 15;
const SAVE_TIMEOUT_MS = 300;

class PackageEditModal extends Component {
  constructor(props) {
    super(props);

    // Use packageGroups prop if available (lazy loaded), otherwise fall back to finding groups
    const packageGroups = props.packageGroups && props.packageGroups.length > 0
      ? props.packageGroups
      : this.getPackageGroups(props.selectedPackage, props.groups);

    this.state = {
      packageName: props.selectedPackage ? props.selectedPackage.name : '',
      packageCode: '',
      codePreview: '',
      selectedGroupCodes: packageGroups.map(g => g.code),
      isCreatingNewGroup: false,
      isSaving: false,
      activeAccordionKey: null,
      guidingTextChanges: {}, // Track guiding text changes: { 'groupId_memberId': 'text' }
      groupNameChanges: {}, // Track group name changes: { 'groupId': 'newName' }
      editingGroupId: null, // Track which group name is being edited
      attachmentChanges: {}, // Track attachment changes: { 'groupId': { min: 0, max: null } }
      skippableChanges: {}, // Track skippable changes per package-group: { 'groupCode': true/false }
      pendingNewGroups: [], // Groups created locally but not yet saved to server (for new package creation)
    };

    // Use ref for timeout to avoid memory leaks
    this.saveTimeoutRef = null;
  }

  getPackageGroups(pkg, allGroups) {
    if (!pkg) return [];
    const packageChecks = lodash.get(pkg, 'spChecks', []);
    return packageChecks
      .map(check => allGroups.find(g => g.code === check.type))
      .filter(Boolean)
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
  }

  // Check if a group is marked as skippable in any existing package
  isGroupSkippableInAnyPackage = (groupCode) => {
    const { packages } = this.props;
    if (!packages || packages.length === 0) return false;

    return packages.some(pkg => {
      const spChecks = lodash.get(pkg, 'spChecks', []);
      const spCheck = spChecks.find(check => check.type === groupCode);
      return spCheck && spCheck.isOptional;
    });
  }

  componentDidUpdate(prevProps) {
    // No longer needed - component doesn't receive isLoading prop
  }

  componentWillUnmount() {
    // Clean up any pending timeouts
    if (this.saveTimeoutRef) {
      clearTimeout(this.saveTimeoutRef);
      this.saveTimeoutRef = null;
    }
  }

  handleCreateNewGroupClick = () => {
    this.setState({
      isCreatingNewGroup: true,
      activeAccordionKey: 'new-group'
    });
  }

  handleSelectExistingGroup = (groupCode) => {
    const { selectedGroupCodes } = this.state;
    if (!selectedGroupCodes.includes(groupCode)) {
      this.setState({
        selectedGroupCodes: [...selectedGroupCodes, groupCode],
      });
    }
  }

  handleRemoveGroup = (groupCode) => {
    this.setState({
      selectedGroupCodes: this.state.selectedGroupCodes.filter(c => c !== groupCode),
      // Also remove from pending groups if it's a pending group
      pendingNewGroups: this.state.pendingNewGroups.filter(g => g.code !== groupCode),
    });
  }

  handleSaveNewGroup = (groupData) => {
    const { onCreateGroup, selectedPackage } = this.props;
    const { pendingNewGroups } = this.state;

    // For NEW packages: store group locally, don't save to server yet
    if (!selectedPackage) {
      // Create a local representation of the group for display
      const localGroup = {
        id: `pending-${Date.now()}`, // Temporary ID for local tracking
        name: groupData.name,
        code: groupData.code,
        screeningGroupMembers: groupData.group_members.map((m, idx) => ({
          id: `pending-member-${idx}`,
          type: m.type,
          guidingText: m.guiding_text,
        })),
        minAttachmentCount: 0,
        maxAttachmentCount: null,
        isOptional: groupData.is_optional || false,
        _isPending: true, // Flag to identify pending groups
        _originalData: groupData, // Store original data for server creation later
      };

      this.setState({
        selectedGroupCodes: [groupData.code, ...this.state.selectedGroupCodes],
        pendingNewGroups: [...pendingNewGroups, localGroup],
        isCreatingNewGroup: false,
        activeAccordionKey: groupData.code,
      });

      message.success('Group added to package');
      return Promise.resolve(localGroup);
    }

    // For EXISTING packages: save to server immediately (original behavior)
    return onCreateGroup(groupData)
      .then((createdGroup) => {
        // Add the new group to the top of selected groups and auto-open it
        this.setState({
          selectedGroupCodes: [groupData.code, ...this.state.selectedGroupCodes],
          isCreatingNewGroup: false,
          activeAccordionKey: groupData.code,
        });
        // Check if group was reactivated - message already shown in handleCreateGroup
        const wasReactivated = createdGroup.data && createdGroup.data._meta && createdGroup.data._meta.reactivated;
        if (!wasReactivated) {
          message.success('Group created successfully');
        }
      })
      .catch((error) => {
        message.error('Failed to create group');
        throw error;
      });
  }

  handleCancelNewGroup = () => {
    this.setState({
      isCreatingNewGroup: false,
      activeAccordionKey: null
    });
  }

  handleCollapseAll = () => {
    this.setState({ activeAccordionKey: null });
  }

  handleGuidingTextChange = (groupId, memberId, text) => {
    const key = groupId + '_' + memberId;
    this.setState({
      guidingTextChanges: {
        ...this.state.guidingTextChanges,
        [key]: text,
      },
    });
  }

  handleGroupNameChange = (groupId, newName) => {
    this.setState({
      groupNameChanges: {
        ...this.state.groupNameChanges,
        [groupId]: newName,
      },
    });
  }

  handleStartEditingGroup = (groupId) => {
    this.setState({
      editingGroupId: groupId,
    });
  }

  handleFinishEditingGroup = () => {
    this.setState({
      editingGroupId: null,
    });
  }

  handleAttachmentChange = (groupId, field, value) => {
    const { groups, packageGroups } = this.props;
    // Look in both groups and packageGroups (lazy loaded)
    let group = groups.find(g => g.id === groupId);
    if (!group && packageGroups) {
      group = packageGroups.find(g => g.id === groupId);
    }
    const currentChanges = this.state.attachmentChanges[groupId] || {};

    // Cap values at max attachments limit
    if (value !== null && value > MAX_ATTACHMENTS) {
      value = MAX_ATTACHMENTS;
    }

    // Get current values (considering any unsaved changes)
    const currentMin = currentChanges.min !== undefined ? currentChanges.min : (group.minAttachmentCount || 0);
    const currentMax = currentChanges.max !== undefined ? currentChanges.max : group.maxAttachmentCount;

    const newChanges = {
      ...currentChanges,
      [field]: value,
    };

    // Auto-adjust max if min is being changed and becomes greater than max
    if (field === 'min') {
      // If max is not null and min > max, set max = min (capped at max limit)
      if (currentMax !== null && value > currentMax) {
        newChanges.max = Math.min(value, MAX_ATTACHMENTS);
      }
      // If max is null (unlimited) and min > 0, set max = min (to enable attachments)
      else if (currentMax === null && value > 0) {
        newChanges.max = Math.min(value, MAX_ATTACHMENTS);
      }
    }
    // Prevent max from being set lower than min
    else if (field === 'max') {
      // If max is being set to a value and it's less than min, set it to min
      if (value !== null && value < currentMin) {
        newChanges.max = currentMin;
      }
    }

    this.setState({
      attachmentChanges: {
        ...this.state.attachmentChanges,
        [groupId]: newChanges,
      },
    });
  }

  handleSkippableChange = (groupCode, isSkippable) => {
    this.setState({
      skippableChanges: {
        ...this.state.skippableChanges,
        [groupCode]: isSkippable,
      },
    });
  }

  areAllGroupsSkippable = (selectedGroups) => {
    const { selectedPackage } = this.props;
    const { skippableChanges } = this.state;
    if (selectedGroups.length === 0) return false;

    // Get spChecks from selected package for existing packages
    const spChecks = selectedPackage ? lodash.get(selectedPackage, 'spChecks', []) : [];

    return selectedGroups.every(group => {
      // Check if there's a local change first
      if (skippableChanges[group.code] !== undefined) {
        return skippableChanges[group.code];
      }
      // For existing packages, get value from spChecks
      const spCheck = spChecks.find(check => check.type === group.code);
      if (spCheck) {
        return spCheck.isOptional || false;
      }
      // For new packages, check if skippable in any other package
      return group.isOptional || this.isGroupSkippableInAnyPackage(group.code);
    });
  }

  handleSkipAllChange = (checked, selectedGroups) => {
    const newSkippableChanges = { ...this.state.skippableChanges };
    selectedGroups.forEach(group => {
      newSkippableChanges[group.code] = checked;
    });
    this.setState({ skippableChanges: newSkippableChanges });
  }

  handleCodeChange = (e) => {
    const rawValue = e.target.value;
    // Automatically transform to lowercase and replace non-alphanumeric with hyphens (URL-safe)
    const transformedValue = rawValue
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // Replace any non-alphanumeric with hyphen
      .replace(/^-+|-+$/g, '') // Strip leading/trailing hyphens
      .substring(0, MAX_PACKAGE_CODE_LENGTH);

    this.setState({
      packageCode: rawValue, // Keep original for input field
      codePreview: transformedValue, // Show transformed version
    });
  }

  handleSavePackage = () => {
    const { selectedPackage, groups, packageGroups, onSave, onCreateGroup, onUpdateGuidingText, onUpdateGroupName, onUpdateGroupAttachments } = this.props;
    const { packageName, packageCode, codePreview, selectedGroupCodes, guidingTextChanges, groupNameChanges, attachmentChanges, skippableChanges, pendingNewGroups } = this.state;

    if (!packageName || !packageName.trim()) {
      message.error('Package name is required');
      return;
    }

    if (selectedGroupCodes.length === 0) {
      message.error('At least one screening group is required');
      return;
    }

    // Validate package code for new packages
    if (!selectedPackage && (!packageCode || !packageCode.trim())) {
      message.error('Package code is required');
      return;
    }

    // Validate package code format for new packages (URL-safe)
    if (!selectedPackage) {
      const finalCode = codePreview || packageCode.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      if (!/^[a-z0-9-]+$/.test(finalCode)) {
        message.error('Package code must contain only lowercase letters, numbers, and hyphens (URL-safe)');
        return;
      }
      if (finalCode.length > MAX_PACKAGE_CODE_LENGTH) {
        message.error(`Package code must be ${MAX_PACKAGE_CODE_LENGTH} characters or less`);
        return;
      }
    }

    this.setState({ isSaving: true });

    // Merge groups and packageGroups to find group data (needed for attachment changes)
    const allGroupsById = new Map();
    groups.forEach(g => allGroupsById.set(g.id, g));
    if (packageGroups) {
      packageGroups.forEach(g => allGroupsById.set(g.id, g));
    }

    // For NEW packages: first create all pending groups on the server
    // Use latest skippable value from skippableChanges if modified, otherwise use original
    const createPendingGroupsPromise = !selectedPackage && pendingNewGroups.length > 0
      ? Promise.all(pendingNewGroups.map(group => {
        const groupData = { ...group._originalData };
        // Override is_optional if it was changed via the checkbox
        if (skippableChanges[group.code] !== undefined) {
          groupData.is_optional = skippableChanges[group.code];
        }
        return onCreateGroup(groupData);
      }))
      : Promise.resolve([]);

    createPendingGroupsPromise
      .then(() => {
        const packageData = {
          name: packageName.trim(),
          screening_group: selectedGroupCodes,
        };

        // Build optional_screening_groups for both new and existing packages
        const optionalScreeningGroups = {};

        if (selectedPackage) {
          packageData.id = selectedPackage.id;

          // For existing packages, get current values from spChecks
          const existingChecks = lodash.get(selectedPackage, 'spChecks', []);
          existingChecks.forEach(check => {
            optionalScreeningGroups[check.type] = check.isOptional || false;
          });
        } else {
          packageData.code = codePreview || packageCode.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

          // For new packages, get optional values from pending new groups
          pendingNewGroups.forEach(group => {
            optionalScreeningGroups[group.code] = group.isOptional || false;
          });
        }

        // Override with any local changes made in skippableChanges
        Object.entries(skippableChanges).forEach(([groupCode, isSkippable]) => {
          optionalScreeningGroups[groupCode] = isSkippable;
        });

        // Include all selected groups - check if skippable in any other package
        selectedGroupCodes.forEach(code => {
          if (optionalScreeningGroups[code] === undefined) {
            optionalScreeningGroups[code] = this.isGroupSkippableInAnyPackage(code);
          }
        });

        packageData.optional_screening_groups = optionalScreeningGroups;

        return onSave(packageData);
      })
      .then(() => {
        // For existing packages only: save group name/attachment/guiding text changes
        // (For new packages, the modal closes after creation)
        if (selectedPackage) {
          // Save all group name changes
          const groupNamePromises = Object.entries(groupNameChanges).map(([groupId, newName]) => {
            return onUpdateGroupName(parseInt(groupId), newName);
          });

          // Save all attachment changes
          const attachmentPromises = Object.entries(attachmentChanges).map(([groupId, changes]) => {
            const group = allGroupsById.get(parseInt(groupId));
            const minCount = changes.min !== undefined ? changes.min : (group ? group.minAttachmentCount || 0 : 0);
            const maxCount = changes.max !== undefined ? changes.max : (group ? group.maxAttachmentCount : null);
            return onUpdateGroupAttachments(parseInt(groupId), minCount, maxCount);
          });

          // Save all guiding text changes
          const guidingTextPromises = Object.entries(guidingTextChanges).map(([key, text]) => {
            const [groupId, memberId] = key.split('_');
            return onUpdateGuidingText(parseInt(groupId), parseInt(memberId), text);
          });

          // Note: skippable changes are now included in packageData.skipped_groups

          return Promise.all([...groupNamePromises, ...attachmentPromises, ...guidingTextPromises]);
        }
        return Promise.resolve();
      })
      .then(() => {
        // Clear tracked changes
        this.setState({ guidingTextChanges: {}, groupNameChanges: {}, attachmentChanges: {}, skippableChanges: {}, pendingNewGroups: [] });

        // Notify parent that all saves are complete so it can refresh data
        if (this.props.onAllSavesComplete) {
          this.props.onAllSavesComplete();
        }

        // Clear isSaving after a short delay to allow parent to refresh
        this.saveTimeoutRef = setTimeout(() => {
          this.setState({ isSaving: false });
          this.saveTimeoutRef = null;
        }, SAVE_TIMEOUT_MS);
      })
      .catch(() => {
        this.setState({ isSaving: false });
      });
  }

  renderGroupContent(group) {
    const { accreditationDefinitions, selectedPackage } = this.props;
    const { guidingTextChanges, attachmentChanges, skippableChanges } = this.state;

    // Get the isOptional value: for existing packages use spChecks, for new packages check if skippable in any other package
    const spCheck = selectedPackage
      ? lodash.get(selectedPackage, 'spChecks', []).find(check => check.type === group.code)
      : null;
    const groupOptional = spCheck
      ? spCheck.isOptional
      : (group.isOptional || this.isGroupSkippableInAnyPackage(group.code));
    const members = lodash.get(group, 'screeningGroupMembers', [])
      .sort((a, b) => (a.type || '').localeCompare(b.type || ''));

    // Get current attachment values
    const currentAttachments = attachmentChanges[group.id] || {};
    const minAttachment = currentAttachments.min !== undefined ? currentAttachments.min : (group.minAttachmentCount || 0);
    const maxAttachment = currentAttachments.max !== undefined ? currentAttachments.max : group.maxAttachmentCount;

    return (
      <div>
        {/* Group Configuration Section */}
        <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ marginBottom: '8px', color: '#595959', fontWeight: 'bold' }}>Group Configuration</h4>

          {/* Attachments row */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '16px' }}>
            <div style={{ minWidth: '120px', paddingBottom: '8px' }}>
              <strong style={{ fontSize: '14px', color: '#595959' }}>
                Attachments
                <Tooltip title={`Set max to 1-${MAX_ATTACHMENTS} to allow attachments, or leave empty to disable. If you set a minimum, users must upload at least that many files. Max will auto-adjust if needed.`}>
                  <Icon type="info-circle" style={{ marginLeft: '6px', color: '#1890ff', cursor: 'help' }} />
                </Tooltip>
              </strong>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                Minimum
              </label>
              <Input
                type="number"
                min="0"
                max={MAX_ATTACHMENTS}
                value={minAttachment}
                onChange={(e) => {
                  const val = e.target.value;
                  this.handleAttachmentChange(group.id, 'min', val === '' ? '' : parseInt(val, 10));
                }}
                onBlur={() => {
                  const cur = this.state.attachmentChanges[group.id];
                  if (cur && (cur.min === '' || cur.min === null || isNaN(cur.min))) {
                    this.handleAttachmentChange(group.id, 'min', 0);
                  }
                }}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                Maximum
              </label>
              <Input
                type="number"
                min="0"
                max={MAX_ATTACHMENTS}
                value={maxAttachment === null ? '' : maxAttachment}
                onChange={(e) => {
                  const val = e.target.value;
                  this.handleAttachmentChange(group.id, 'max', val === '' ? null : parseInt(val, 10));
                }}
                placeholder="Leave empty or set to 0 for no attachments"
                style={{ width: '100%' }}
              />
            </div>
          </div>
          {/* Can be skipped setting - per package */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={skippableChanges[group.code] !== undefined ? skippableChanges[group.code] : groupOptional}
              onChange={(e) => this.handleSkippableChange(group.code, e.target.checked)}
            >
              Can be skipped
            </Checkbox>
            <Tooltip title="When enabled, users can skip this accreditation group during screening without providing any details.">
              <Icon type="info-circle" style={{ marginLeft: '6px', color: '#1890ff', cursor: 'help' }} />
            </Tooltip>
          </div>

        </div>

        {/* Guiding Text Configuration Section */}
        <div>
          <h4 style={{ marginBottom: '12px', color: '#595959', fontWeight: 'bold' }}>Guiding Text Configuration</h4>
          {members.length === 0 ? (
            <p style={{ color: '#999', fontStyle: 'italic', margin: '8px 0' }}>No members in this group</p>
          ) : (
            <div className="screening-group-members">
              {members.map((member, index) => {
                const definition = accreditationDefinitions.find(d => d.typeCode === member.type);
                const typeName = definition ? definition.typeName : member.type;
                const key = group.id + '_' + member.id;
                const currentValue = guidingTextChanges[key] !== undefined ? guidingTextChanges[key] : (member.guidingText || '');

                return (
                  <div key={index} className="member-item">
                    <div className="member-header">
                      <strong>{typeName}</strong>
                      <Tag style={{ marginLeft: '8px' }}>{member.type}</Tag>
                    </div>
                    <div className="member-guiding-text">
                      <InlineGuidingTextEditor
                        value={currentValue}
                        onChange={(newText) => this.handleGuidingTextChange(group.id, member.id, newText)}
                        placeholder="Enter guiding text for this accreditation type"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const {
      visible,
      onClose,
      selectedPackage,
      groups,
      packageGroups,
      accreditationDefinitions,
    } = this.props;

    const { packageName, packageCode, codePreview, selectedGroupCodes, isCreatingNewGroup, isSaving, activeAccordionKey, pendingNewGroups } = this.state;

    // Merge packageGroups (lazy-loaded with full data) with all groups
    // packageGroups has the detailed data, groups has all available groups for selection
    const allGroupsMap = new Map();
    groups.forEach(g => allGroupsMap.set(g.code, g));
    // Override with packageGroups data (which has complete member info)
    if (packageGroups) {
      packageGroups.forEach(g => allGroupsMap.set(g.code, g));
    }
    // Add pending new groups (not yet saved to server)
    pendingNewGroups.forEach(g => allGroupsMap.set(g.code, g));

    // Get selected groups and sort by code
    const selectedGroups = selectedGroupCodes
      .map(code => allGroupsMap.get(code))
      .filter(Boolean)
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''));

    // Get available groups to add (not already selected, exclude dynamic groups)
    const dynamicSuffix = '-' + String(this.props.organizationId);
    const availableGroups = groups
      .filter(g => !selectedGroupCodes.includes(g.code))
      .filter(g => !(g.code && g.code.endsWith(dynamicSuffix)))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return (
      <Modal
        visible={visible}
        title={selectedPackage ? `Edit Screening Package: ${selectedPackage.name}` : 'Create New Package'}
        width={900}
        onCancel={isSaving ? undefined : onClose}
        footer={null}
        closable={!isSaving}
        destroyOnClose={true}
      >
        {isSaving ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
          }}>
            <Spin size="large" tip="Saving changes..." />
          </div>
        ) : (
          <div className="package-edit-modal-content">
            {/* Sticky Action Buttons */}
            <div style={{
              position: 'sticky',
              top: 92,
              height: 0,
              zIndex: 100,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              pointerEvents: 'none',
            }}>
              {/* Spacer for collapse button */}
              <div style={{ flex: 1 }}></div>

              {/* Action buttons on the right */}
              <div style={{
                display: 'flex',
                gap: '8px',
                pointerEvents: 'auto',
                position: 'relative',
                top: '-68px', // When stuck at 60, buttons appear at 24px to center with collapse button
              }}>
                <Button onClick={onClose}>
                  Cancel
                </Button>
                <Button type="primary" onClick={this.handleSavePackage} loading={isSaving}>
                  {selectedPackage ? 'Save Changes' : 'Create Package'}
                </Button>
              </div>
            </div>

            {/* Package Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Package Name
              </label>
              <Input
                value={packageName}
                onChange={(e) => this.setState({ packageName: e.target.value })}
                placeholder="e.g., Standard Screening Package"
              />
            </div>

            {/* Package Code */}
            {selectedPackage ? (
              <div style={{ marginBottom: '20px', color: '#666', fontSize: '13px' }}>
                Code: <Tag>{selectedPackage.code}</Tag>
                <Tooltip title="Package code cannot be edited after package is created." overlayStyle={{ maxWidth: '250px' }}>
                  <Icon type="info-circle" style={{ marginLeft: '6px', color: '#000', fontSize: '12px' }} />
                </Tooltip>
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Package Code
                  <Tooltip title={
                    <div>
                      This code is a unique identifier<br />
                      specific to the package<br />
                      that will show up in the<br />
                      URL of this screening<br />
                      package.
                    </div>
                  } overlayStyle={{ maxWidth: '250px' }}>
                    <Icon type="info-circle" style={{ marginLeft: '6px', color: '#000', fontSize: '12px' }} />
                  </Tooltip>
                </label>
                <Input
                  value={packageCode}
                  onChange={this.handleCodeChange}
                  placeholder="e.g., std-pkg"
                  maxLength={MAX_PACKAGE_CODE_LENGTH}
                />
                {codePreview && (
                  <small style={{ color: '#52c41a', display: 'block', marginTop: '4px' }}>
                    Will be saved as: <strong>{codePreview}</strong>
                  </small>
                )}
                {!codePreview && (
                  <small style={{ color: '#999', display: 'block', marginTop: '4px' }}>
                    Lowercase letters, numbers, and hyphens only (URL-safe, max {MAX_PACKAGE_CODE_LENGTH} chars)
                  </small>
                )}
              </div>
            )}

            <Divider>
              Accreditation Groups
              <Tooltip title="Individuals are only required to complete details for one accreditation in an accreditation group. " overlayStyle={{ maxWidth: '300px' }}>
                <Icon type="info-circle" style={{ marginLeft: '6px', color: '#000', fontSize: '12px' }} />
              </Tooltip>
            </Divider>

            {/* Add Group Section */}
            <div style={{ marginBottom: '16px' }}>
              {/* Create new button and search existing on same line */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                <Button type="primary" icon="plus" onClick={this.handleCreateNewGroupClick}>
                  Create New Group
                </Button>

                {availableGroups.length > 0 && (
                  <>
                    <span style={{ color: '#999', padding: '0 8px' }}>or</span>
                    <Select
                      showSearch
                      placeholder="Search and select an additional accreditation group"
                      style={{ flex: 1 }}
                      onChange={this.handleSelectExistingGroup}
                      value={undefined}
                      filterOption={(input, option) => {
                        const label = option.props.label || '';
                        return label.toLowerCase().includes(input.toLowerCase());
                      }}
                      optionFilterProp="label"
                      notFoundContent="No groups found"
                    >
                      {availableGroups.map(group => (
                        <Select.Option
                          key={group.code}
                          value={group.code}
                          label={group.name + ' ' + group.code}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{group.name}</span>
                            <Tag>{group.code}</Tag>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#666' }}>
                  {selectedGroups.length} {selectedGroups.length === 1 ? 'group' : 'groups'}
                  {selectedGroups.length > 0 && <span style={{ marginLeft: '8px', fontSize: '12px', fontStyle: 'italic' }}>(ordered by code)</span>}
                </span>
              </div>
            </div>

            {/* Collapse All Button - Shows when accordion sections are open */}
            {activeAccordionKey && (selectedGroups.length > 0 || isCreatingNewGroup) && (
              <div style={{
                position: 'sticky',
                top: 60,
                height: 0,
                zIndex: 10,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <Button
                  size="small"
                  onClick={this.handleCollapseAll}
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    pointerEvents: 'auto',
                    position: 'relative',
                    top: '-32px',
                  }}
                  icon="minus-square"
                >
                  Collapse All
                </Button>
              </div>
            )}

            {/* Groups Accordion */}
            <Collapse
              bordered={true}
              expandIconPosition="right"
              activeKey={activeAccordionKey}
              onChange={(key) => {
                // Handle both accordion mode (single key) and non-accordion mode (array of keys)
                // If no panels are open, set to null to hide the collapse button
                const newKey = (Array.isArray(key) && key.length === 0) || !key ? null : key;
                this.setState({ activeAccordionKey: newKey });
              }}
            >
              {/* New Group Creation Panel - Always at top when visible */}
              {isCreatingNewGroup && (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Tag color="green">NEW</Tag>
                      <strong>Create New Accreditation Group</strong>
                    </div>
                  }
                  key="new-group"
                  forceRender={true}
                >
                  <GroupCreationPanel
                    accreditationTypes={accreditationDefinitions}
                    existingGroups={groups}
                    selectedGroupCodes={selectedGroupCodes}
                    onSave={this.handleSaveNewGroup}
                    onCancel={this.handleCancelNewGroup}
                  />
                </Panel>
              )}

              {/* Existing/Selected Groups */}
              {selectedGroups.map(group => {
                const memberCount = lodash.get(group, 'screeningGroupMembers', []).length;
                const currentGroupName = this.state.groupNameChanges[group.id] !== undefined
                  ? this.state.groupNameChanges[group.id]
                  : group.name;
                const isEditingName = this.state.editingGroupId === group.id;

                return (
                  <Panel
                    header={
                      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '8px' }}>
                          {/* Group Name */}
                          {isEditingName ? (
                            <Input
                              value={currentGroupName}
                              onChange={(e) => {
                                e.stopPropagation();
                                this.handleGroupNameChange(group.id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={this.handleFinishEditingGroup}
                              onPressEnter={this.handleFinishEditingGroup}
                              autoFocus
                              style={{
                                maxWidth: '250px',
                                fontWeight: 'bold',
                              }}
                            />
                          ) : (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                this.handleStartEditingGroup(group.id);
                              }}
                              style={{
                                fontWeight: 'bold',
                                cursor: 'text',
                                transition: 'text-decoration 0.2s ease-in',
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.textDecoration = 'underline';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.textDecoration = 'none';
                              }}
                            >
                              {currentGroupName}
                            </span>
                          )}

                          {/* Group Code - Non-editable */}
                          <Tag>{group.code}</Tag>

                          <span style={{ marginLeft: '8px', color: '#666', fontSize: '12px' }}>
                            {memberCount} {memberCount === 1 ? 'member' : 'members'}
                          </span>
                        </div>
                        <Button
                          size="small"
                          type="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.handleRemoveGroup(group.code);
                          }}
                          style={{ marginLeft: '8px' }}
                        >
                          Remove
                        </Button>
                      </div>
                    }
                    key={group.code}
                  >
                    {this.renderGroupContent(group)}
                  </Panel>
                );
              })}
            </Collapse>

            {/* Can be skipped-all setting - After creating */}
            {selectedGroups.length > 0 && (
              <div style={{ marginTop: '16px', marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                <Checkbox
                  checked={this.areAllGroupsSkippable(selectedGroups)}
                  onChange={(e) => this.handleSkipAllChange(e.target.checked, selectedGroups)}
                >
                  All groups can be skipped
                </Checkbox>
                <Tooltip title="When enabled, marks all accreditation groups in this package as skippable.
              Users can skip any group during screening without providing details.
              This will show an incomplete check in the ICR Tracking table.">
                  <Icon type="info-circle" style={{ marginLeft: '6px', color: '#1890ff', cursor: 'help' }} />
                </Tooltip>
              </div>
            )}

            {selectedGroups.length === 0 && !isCreatingNewGroup && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No accreditation groups added to this screening package yet. Click 'Create new group' or select an existing group to get started.
              </div>
            )}
          </div>
        )}
      </Modal>
    );
  }
}

PackageEditModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCreateGroup: PropTypes.func.isRequired,
  selectedPackage: PropTypes.object,
  groups: PropTypes.array.isRequired,
  packages: PropTypes.array,
  packageGroups: PropTypes.array,
  accreditationDefinitions: PropTypes.array.isRequired,
  onUpdateGuidingText: PropTypes.func.isRequired,
  onUpdateGroupName: PropTypes.func.isRequired,
  onUpdateGroupAttachments: PropTypes.func.isRequired,
  onAllSavesComplete: PropTypes.func,
};

PackageEditModal.defaultProps = {
  selectedPackage: null,
  packages: [],
  packageGroups: [],
  onAllSavesComplete: null,
};

export default PackageEditModal;
